declare var API_URL: string;
declare var VERSION: string;
declare var SENTRY_RELEASE: string;
declare var APP_KEY: string;
declare var AZURE_CLIENT_ID: string;
declare var OAUTH_2_ID: string;
declare var RUNTIME_MODE: 'development' | 'production';

import { StaticFieldNames, TemplateStringEnum } from "./enums";
import type { JiraAddonSettings, JiraAddonTemplate } from "./interfaces";
import type { Jira } from "@yasoonofficial/runtime";
import { GraphScope } from "@yasoonofficial/runtime";
import { JiraHosting } from "shared/utils/enums";

export const RuntimeMode = RUNTIME_MODE;
export const CurrentVersion = VERSION;
export const SentryRelease = SENTRY_RELEASE;
export const AtlassianAppKey = APP_KEY;
export const AzureClientId = AZURE_CLIENT_ID;
export const OAuth2Id = OAUTH_2_ID;

export function getApiUrlByHosting(hosting: JiraHosting): string {
    if (RuntimeMode === "development") {
        return API_URL;
    }
    if (hosting === JiraHosting.server) {
        return "https://office365.yasoon.com/v2";
    } else {
        return "https://office365.yasoon.com/v3";
    }
}
//Sadly in some @atlaskit components it is now checked whether process.env.NODE_ENV !== "production" leading to Error: "env" of undefined
//@ts-expect-error
window['process'] = {
    env: {
        NODE_ENV: RUNTIME_MODE
    }
};

export const XDomainSlaveScriptUrl = '/plugins/servlet/jiraforoutlook/proxy';

//Mail Information Header - Attachment constant
export const AttachmentMailHeaderHref = 'internal-attachment-link://';
export const EmailAttachmentCollection = 'email-attachments';

export const InstanceFieldMeta: Jira.HarmonizedMetaField = {   //this is our field
    required: true,
    key: StaticFieldNames.instance,
    schema: {
        type: 'string',
        system: 'custom'    //we have to use something!
    }
};

export const ProjectFieldMeta: Jira.HarmonizedMetaField = {
    required: true,
    key: StaticFieldNames.project,
    schema: {
        type: 'project',
        system: 'project'
    }
};

export const IssueTypeMeta: Jira.HarmonizedMetaField = {
    required: true,
    key: StaticFieldNames.issueType,
    schema: {
        type: 'issuetype'
    }
};

export const IssueMeta: Jira.HarmonizedMetaField = {
    required: true,
    key: StaticFieldNames.issue,
    schema: {
        type: 'issue'
    }
};

export const RequestTypeMeta: Jira.HarmonizedMetaField = {
    required: true,
    key: StaticFieldNames.requesttype,
    schema: {
        type: 'requesttype'
    }
};

export const CommentMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.comment,
    schema: {
        type: 'string'
    }
};

export const ParticipantsMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.participants,
    schema: {
        type: 'user'
    }
};

export const OnBehalfOfMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.onBehalfOf,
    schema: {
        type: 'user'
    }
};

export const AttachmentMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.attachment,
    schema: {
        type: 'attachment'
    }
};

export const originalEstimateFieldMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.originalEstimate,
    name: 'Original Estimate',
    schema: {
        type: 'timetracking'
    }
};

export const remainingEstimateFieldMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.remainingEstimate,
    name: 'Remaining Estimate',
    schema: {
        type: 'timetracking'
    }
};

export const timeSpentMeta: Jira.HarmonizedMetaField = {
    required: false,
    key: StaticFieldNames.timeSpent,
    name: 'Time Spent',
    schema: {
        type: 'timetracking'
    }
};

export const NewOptionPrefix = '<new>_';

export const HostNameCOM = 'OutlookCOM';

export const JiraServerAddonRoutes = {
    info: '/rest/com.yasoon.jira.outlook-email/1.0/public/info',
    infoServlet: '/plugins/servlet/jiraforoutlook/public/info',
    config: '/rest/com.yasoon.jira.outlook-email/1.0/property/oefj_globalSettings',
    requestAddon: '/rest/plugins/1.0/requests',
    getContextJwt: '/rest/com.yasoon/1.0/public/jwt',
};

export const hiddenGroup = {
    id: "-1",
    name: 'Hidden Requesttypes'
};

export const InsightRoutes = {
    getDefaultObjects: (fieldConfig: string) => `/rest/insight/1.0/customfield/default/${fieldConfig}/objects`,
    getReferenceObjects: (fieldConfig: string) => `/rest/insight/1.0/customfield/reference/${fieldConfig}/objects`
};

export const StagilAssetRoutes = {
    getAssetRelations: (payload: {
        fieldConfigId: string,
        projectId: string,
        typeId: string,
        query: string
    }) => `/rest/soj/1.0/issue/picker/fieldConfigId/${payload.fieldConfigId}?typeId=${payload.typeId}&projectId=${payload.projectId}&currentIssue=&query=${payload.query}`
};

export const TempoServerRoutes = {
    getTeams: () => `/rest/tempo-teams/1/team`,
    getAccounts: () => `/rest/tempo-accounts/1/account`,
    getAccountsByProject: (projectId: string) => `/rest/tempo-accounts/1/account/project/${projectId}`
};

export const TempoCloudRoutes = {
    selectableOptions: (fieldId: string, projectId?: string) => {
        let query = `maxResults=500`;
        if (projectId)
            query += `&projectId=${encodeURIComponent(projectId)}`;

        return `/rest/api/3/field/${encodeURIComponent(fieldId)}/option/suggestions/edit?${query}`;
    }
};

export enum TeamleadCRMTableNames {
    Contacts = "CONTACTS"
}

export const TeamleadCRMRoutes = {
    searchCRMEntities: (tableName: TeamleadCRMTableNames, input: string, excludeOptions: string[], projectId: number, projectName: string, fieldId: string): string => {
        let queryExcludes: string = "";
        if (excludeOptions.length > 0)
            queryExcludes = excludeOptions.map(name => "&exclude=" + name).join("");
        else
            queryExcludes = "&exclude=";

        return `/rest/catalog-rest/1.0/catalog/searchCRMEntities?tableName=${tableName}&isCF=true&query=${input}${queryExcludes}&projectId=${projectId}&projectName=${projectName}&customfieldId=${fieldId}`;
    },
};

export const JiraPortfolioRoutes = {
    findTeams: `/rest/teams/1.0/teams/find`,
    findParentLinks: `/rest/jpo/1.0/parent/suggest`,
};

export const DefaultAssignee: Partial<Jira.User> = {
    accountId: '-1',
    key: '-1',
    name: '-1',
    displayName: 'Automatic',
    avatarUrls: {
        "48x48": 'resources/images/icons/lightbulb.svg',
        "24x24": 'resources/images/icons/lightbulb.svg',
        "16x16": 'resources/images/icons/lightbulb.svg',
        "32x32": 'resources/images/icons/lightbulb.svg'
    }
};

export const UnassignedUser: Partial<Jira.User> = {
    key: null,
    name: null,
    displayName: 'Unassigned',
    avatarUrls: {
        "48x48": '',
        "24x24": '',
        "16x16": '',
        "32x32": ''
    }
};

export const DefaultTemplate: JiraAddonTemplate = {
    id: 'default',
    name: 'Default Template',
    criteria: {
        projectId: '-1',
        issueTypeId: '-1',
        userGroup: '-1'
    },
    fieldMapping: {
        summary: TemplateStringEnum.SUBJECT,
        reporter: TemplateStringEnum.SENDER,
        description: TemplateStringEnum.BODY,
    },
    settings: {
        addEmailHeader: 'top',
        preselectAttachments: true
    }
};

export const DefaultSettings: JiraAddonSettings = {
    contexts: {
        data: [DefaultTemplate],
        order: {
            default: 0,
        },
        multiContextEnabled: false
    },
    globalSettings: {
        errorLoggingEnabled: true,
        onboardingEmailsEnabled: true,
    },
    technicalSettings: {
        logrocketEnabled: true,
        sentryEnabled: true,
        autoRefreshFeed: true,
    }
};

export const DefaultFieldOrder = [
    StaticFieldNames.summary,
    StaticFieldNames.reporter,
    StaticFieldNames.assignee,
    StaticFieldNames.components,
    StaticFieldNames.description,
    StaticFieldNames.fixVersions,
    StaticFieldNames.priority,
    StaticFieldNames.labels,
    StaticFieldNames.attachment,
    StaticFieldNames.duedate,
];

export enum NfeedSchemaNames {
    standardTextField = 'com.valiantys.jira.plugins.SQLFeed:nfeed-standard-customfield-type',
    legacyStandardTextField = 'com.valiantys.jira.plugins.SQLFeed:com.valiantys.jira.plugin.sqlfeed.customfield.type',
    standardUserField = 'com.valiantys.jira.plugins.SQLFeed:com.valiantys.jira.plugins.sqlfeed.user.customfield.type',
    snapshotTextField = 'com.valiantys.jira.plugins.SQLFeed:nfeed-unplugged-customfield-type',
    snapshotDateTimeField = 'com.valiantys.jira.plugins.SQLFeed:nfeed-datetime-customfield-type',
    snapshotDateField = 'com.valiantys.jira.plugins.SQLFeed:nfeed-date-customfield-type',
}

export enum ScriptRunnerSchemaNames {
    singleDatabasePicker = 'com.onresolve.jira.groovy.groovyrunner:single-database-picker-cf',
    multiDatabasePicker = 'com.onresolve.jira.groovy.groovyrunner:multiple-database-picker-cf',
}

export enum VlinksSchemaNames {
    multi = 'net.veniture.jira.vLinks:vLinks-multi-cf',
    single = 'net.veniture.jira.vLinks:vLinks-single-cf',
    //currently not supported, but maybe in the future
    // linked = 'net.veniture.jira.vLinks:vLinks-linked-cf',
}

/**
 * These fields were made by third parties but later bought by Jira so they still have customIds
 * only add fields here if they are unique within a createMeta
 *
 * You can use a JiraUtilitie to getCustomField(Id)
 */
export enum CustomSchemaNames {
    participants = 'com.atlassian.servicedesk:sd-request-participants',
    organization = 'com.atlassian.servicedesk:sd-customer-organizations',
    epicLink = 'com.pyxis.greenhopper.jira:gh-epic-link',
    parentLink = 'com.atlassian.jpo:jpo-custom-field-parent'
}

export const lozengeColors = ['default', 'inprogress', 'moved', 'new', 'removed', 'success'] as const;
export type LozengeColor = typeof lozengeColors[number];
export function isOfTypeLozengeColor(input: string): input is LozengeColor {
    return (lozengeColors as readonly string[]).includes(input);
}

export enum ShareItemVisibilityState {
    enabled = "enabled",
    disabled = "disabled",
    autoDetected = "autoDetected",
}

export const EmptyTextNodeContent = ' ';

export const MinimalSidebarRenderFieldIds = ["description", "issuetype", "priority", "status", "duedate", "assignee", "reporter"];

export const GraphScopes: GraphScope[] = [
    "Mail.ReadWrite.Shared",
    "Mail.Send.Shared",
    "People.Read",
    "User.Read",
    "User.ReadBasic.All",
    "profile"
];
